






















import { computed, ComputedRef, defineComponent, PropType, Ref, ref, watch, toRefs } from '@vue/composition-api';
import { addDays, addMinutes, format } from 'date-fns';
import SelectInput from '@/components/SelectInput.vue';
import { MappedService, useGetAvailableTimes } from '@/features/services';
import { toNonNullable } from '@/utils/collections';
import { SelectOption, Schedule } from '@/types/utils';
import { useConfig } from '@/features/storeConfig';
import { load } from '@/directives/loading';

export default defineComponent({
  name: 'ServiceScheduler',
  model: {
    prop: 'modelValue',
  },
  components: {
    SelectInput,
  },
  directives: {
    load,
  },
  props: {
    modelValue: {
      type: Array as PropType<Schedule[]>,
      default: [],
    },
    idx: {
      type: Number,
      default: 0,
    },
    serviceUid: {
      type: String,
      default: null,
    },
    branchServices: {
      type: Array,
      default: () => [],
    },
    slotsNumber: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const { modelValue, idx, serviceUid, branchServices, slotsNumber } = toRefs(props);
    const { getConfig } = useConfig();

    const model: ComputedRef<Schedule[]> = computed({
      get() {
        return modelValue.value;
      },
      set(value) {
        emit('input', value);
      },
    });
    const serviceStoreSlots = computed(() => {
      if (branchServices.value.length) {
        return (branchServices.value.find((s: MappedService) => s.uid === serviceUid.value) as MappedService)
          .storeSlotDuration;
      }

      return slotsNumber.value;
    });

    const { getAvailableTimes, isFetchingTimes } = useGetAvailableTimes();

    const dayOptions: Ref<SelectOption[]> = ref([]);
    const servicesTimeOptions: Ref<Array<SelectOption>> = ref([]);

    const today = new Date();

    // Populate array with the next 30 days
    for (let i = 0; i <= 30; i++) {
      dayOptions.value.push({
        value: format(addDays(today, i), 'yyyy-MM-dd'),
        label: addDays(today, i).toLocaleDateString('ar-EG', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }),
      });
    }

    const selectedDate = ref(dayOptions.value[0].value);
    const selectedTime = ref('');
    updateSelectOptions(selectedDate.value);

    watch(selectedDate, async value => {
      const day = value;
      await updateSelectOptions(day);
    });

    watch(selectedTime, value => {
      const time = value;
      modelValue.value[idx.value].time = time;
    });

    watch([serviceUid, branchServices], _ => {
      if (!serviceUid.value || !branchServices.value.length) {
        return;
      }

      updateSelectOptions(selectedDate.value);
    });

    async function updateSelectOptions(day: string) {
      modelValue.value[idx.value] = {
        ...modelValue.value[idx.value],
        day,
      };
      const times = await getAvailableTimes(serviceUid.value as string, day);
      const { slotInMinutes } = await getConfig();
      const slotTime = slotInMinutes;

      servicesTimeOptions.value = toNonNullable(times).map(startTime => {
        const timeInMinutes = serviceStoreSlots.value * slotTime;
        const startDateTime = new Date(`${selectedDate.value} ${startTime}`);
        const endDateTime = addMinutes(startDateTime, timeInMinutes);
        return {
          value: startTime,
          label: `${startDateTime.toLocaleTimeString('ar-EG', {
            hour: '2-digit',
            minute: '2-digit',
          })} - ${endDateTime.toLocaleTimeString('ar-EG', {
            hour: '2-digit',
            minute: '2-digit',
          })}`,
        };
      });
      selectedTime.value = times?.[0] as string;
    }

    return {
      dayOptions,
      servicesTimeOptions,
      model,
      selectedDate,
      selectedTime,
      isFetchingTimes,
    };
  },
});
